import axios from "../../utils/axios";

const CAMPAIGN_DRAFT_ID_STORAGE_KEY = "campaignCreateDraftId";

export type CampaignScope = {
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
};

export type CampaignFlowState = {
  campaignId?: string;
  campaignName?: string;
  scope?: CampaignScope;
  stepOnePayload?: Record<string, any>;
  stepTwoPayload?: Record<string, any>;
  stepThreePayload?: Record<string, any>;
  stepThreeRehydration?: Record<string, any>;
  stepFourPayload?: Record<string, any>;
};

type FlowScopeResolverInput = {
  flowState?: CampaignFlowState | null;
  owner?: any;
  currentUser?: any;
  currentWorkspace?: any;
  draftCampaign?: any;
};

const parseWorkspaceId = (currentWorkspace: any): string | undefined => {
  if (!currentWorkspace) return undefined;
  if (typeof currentWorkspace === "string") {
    try {
      const parsed = JSON.parse(currentWorkspace);
      return parsed?._id || undefined;
    } catch {
      return undefined;
    }
  }
  return currentWorkspace?._id || undefined;
};

export const resolveFlowScope = ({
  flowState,
  owner,
  currentUser,
  currentWorkspace,
  draftCampaign,
}: FlowScopeResolverInput): CampaignScope => {
  const stateScope = flowState?.scope || {};

  const ownerId =
    stateScope.ownerId ||
    flowState?.stepTwoPayload?.senderOwnerId ||
    draftCampaign?.ownerId ||
    owner?._id ||
    undefined;
  const userId =
    stateScope.userId ||
    draftCampaign?.userId ||
    currentUser?.userId ||
    owner?.userId ||
    undefined;
  const workspaceId =
    stateScope.workspaceId ||
    draftCampaign?.workspaceId ||
    parseWorkspaceId(currentWorkspace) ||
    undefined;

  return {
    ownerId: ownerId ? String(ownerId) : undefined,
    userId: userId ? String(userId) : undefined,
    workspaceId: workspaceId ? String(workspaceId) : undefined,
  };
};

export const persistDraftCampaignId = (campaignId?: string) => {
  if (!campaignId) return;
  try {
    localStorage.setItem(CAMPAIGN_DRAFT_ID_STORAGE_KEY, campaignId);
  } catch {
    // ignore storage failures
  }
};

export const clearPersistedDraftCampaignId = () => {
  try {
    localStorage.removeItem(CAMPAIGN_DRAFT_ID_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
};

export const resolveCampaignIdFromStateOrSearch = (
  state: CampaignFlowState | null,
  searchCampaignId: string | null
) => {
  const resolved = state?.campaignId || searchCampaignId || undefined;
  if (resolved) {
    persistDraftCampaignId(resolved);
    return resolved;
  }
  try {
    return localStorage.getItem(CAMPAIGN_DRAFT_ID_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

export const draftSearchFromCampaignId = (campaignId?: string) =>
  campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : "";

export const MAX_CAMPAIGN_LIST_IDS = 10;

export const normalizeCampaignEntityId = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    const record = value as { _id?: unknown; $oid?: unknown };
    if (typeof record._id === "string") return record._id;
    if (typeof record.$oid === "string") return record.$oid;
  }
  try {
    return String(value);
  } catch {
    return "";
  }
};

export const normalizeCampaignListIds = (
  listIds?: unknown,
  listId?: unknown
): string[] => {
  const collected: string[] = [];
  if (Array.isArray(listIds)) {
    for (const raw of listIds) {
      const id = normalizeCampaignEntityId(raw);
      if (id) collected.push(id);
    }
  }
  const single = normalizeCampaignEntityId(listId);
  if (!collected.length && single) collected.push(single);
  return [...new Set(collected)].slice(0, MAX_CAMPAIGN_LIST_IDS);
};

export const hasLinkedCampaignLists = (listIds?: unknown, listId?: unknown) =>
  normalizeCampaignListIds(listIds, listId).length > 0;

export async function upsertCampaignDraft(payload: Record<string, any>) {
  const response = await axios.post("/campaign/upsertDraftCampaign", payload);
  const data = response?.data || {};
  persistDraftCampaignId(data?.campaignId);
  return data;
}

export async function fetchCampaignDraft(payload: Record<string, any>) {
  const response = await axios.post("/campaign/getCampaignDraft", payload);
  const data = response?.data || {};
  persistDraftCampaignId(payload?.campaignId);
  return data;
}

